import { parseCookies } from 'nookies'

export interface StoredConsent {
  analytics: boolean
  advertisement: boolean
  functionality: boolean
}

const ACCEPT_ALL: StoredConsent = {
  analytics: true,
  advertisement: true,
  functionality: true
}

const REJECT_ALL: StoredConsent = {
  analytics: false,
  advertisement: false,
  functionality: false
}

/**
 * The visitor's saved cookie-banner choice, or `null` while they have not made
 * one (nothing is revoked until they do). CookieConsent writes
 * `cookie-consent` ('true' = accept all, 'false' = reject all) and/or
 * `cookie-consent-preferences` (the granular JSON, which wins over 'false').
 * An unreadable preferences cookie reads as "reject all" — a consent check
 * fails closed.
 */
export const readStoredConsent = (): StoredConsent | null => {
  const cookies = parseCookies()
  const consent = cookies['cookie-consent']
  const preferences = cookies['cookie-consent-preferences']
  if (!consent && !preferences) return null
  if (consent === 'true') return ACCEPT_ALL
  if (!preferences) return REJECT_ALL
  try {
    const parsed = JSON.parse(preferences) as Partial<StoredConsent>
    return {
      analytics: parsed.analytics === true,
      advertisement: parsed.advertisement === true,
      functionality: parsed.functionality === true
    }
  } catch {
    return REJECT_ALL
  }
}

/**
 * True only after the visitor turned advertising cookies off. Ad pixels with
 * no consent mode of their own (OpenAI, LinkedIn) must not load or fire then;
 * Meta gets `fbq('consent', 'revoke')` instead.
 */
export const isAdvertisingRejected = (): boolean =>
  readStoredConsent()?.advertisement === false

// Third-party ad/analytics cookies that pixels set on our domain. Mexico has no
// choice before the banner, so a first visit sets them on page 1; without this
// they outlived a "Rechazar todas" click (consent matrix, 2026-10-09). PostHog's
// `ph_*` cookie is deliberately absent — it is where the opt-out itself lives —
// and so are our own UTM/click-ID cookies (attribution, counsel queue).
const ADVERTISING_COOKIE =
  /^(_fbp|_fbc|_gcl_[a-z]+|_gac_[\w-]+|_uetsid|_uetvid|_uetmsclkid|li_fat_id)$/
const ANALYTICS_COOKIE = /^(_ga|_ga_[A-Z0-9]+|_gid|_gat[\w-]*)$/

/** Which of `cookieNames` the visitor's choice says must not exist. */
export const cookiesToClear = (
  cookieNames: string[],
  consent: Pick<StoredConsent, 'advertisement' | 'analytics'>
): string[] =>
  cookieNames.filter(
    (name) =>
      (!consent.advertisement && ADVERTISING_COOKIE.test(name)) ||
      (!consent.analytics && ANALYTICS_COOKIE.test(name))
  )

/**
 * Every domain a pixel may have scoped its cookie to: host-only plus each
 * parent with at least two labels (`www.spotme.mx` → `www.spotme.mx`,
 * `.www.spotme.mx`, `spotme.mx`, `.spotme.mx`). A cookie can only be deleted
 * with the exact domain it was set with, and gtag/fbq pick the top-level one.
 */
export const cookieDomainsFor = (hostname: string): string[] => {
  const labels = hostname.split('.')
  const domains: string[] = []
  for (let i = 0; i <= labels.length - 2; i++) {
    const domain = labels.slice(i).join('.')
    domains.push(domain, `.${domain}`)
  }
  return domains
}

/**
 * Deletes the ad/analytics cookies the stored choice rules out. Runs on every
 * load with a stored choice (not only on the click), so a pixel that slipped a
 * cookie in before consent was applied is cleaned on the next page.
 */
export const clearRejectedCookies = (): void => {
  if (typeof document === 'undefined') return
  const consent = readStoredConsent()
  if (!consent) return
  const names = Object.keys(parseCookies())
  const doomed = cookiesToClear(names, consent)
  if (!doomed.length) return
  const domains = cookieDomainsFor(window.location.hostname)
  for (const name of doomed) {
    document.cookie = `${name}=; Max-Age=0; path=/`
    for (const domain of domains) {
      document.cookie = `${name}=; Max-Age=0; path=/; domain=${domain}`
    }
  }
}
